<script>
import {
    mdiArrowRight,
    mdiAccountGroupOutline,
    mdiClipboardTextOutline,
    mdiLaptop,
    mdiLifebuoy,
    mdiPackageVariantClosed,
    mdiPlus,
    mdiWrenchOutline
} from '@mdi/js'
import RmasSection from './components/rmas_section.vue'
import PartsOrdersSection from './components/parts_orders_section.vue'
import RegisteredProductsSection from './components/registered_products_section.vue'
import SavedQuotesSection from './components/saved_quotes_section.vue'
import OrganizationSection from './components/organization_section.vue'
import HappyfoxSection from './components/happyfox_section.vue'

export default {
    name: 'AccountDashboard',
    components: {
        RmasSection,
        PartsOrdersSection,
        RegisteredProductsSection,
        SavedQuotesSection,
        OrganizationSection,
        HappyfoxSection
    },
    metaInfo: { title: 'Account Dashboard' },
    data() {
        return {
            mdiArrowRight,
            mdiPlus,
            summaries: {},
            organizationLink: '/help-support',
            sections: [
                { id: 'rmas', title: 'RMAs', icon: mdiWrenchOutline, link: '/your-account/rma-history' },
                {
                    id: 'parts',
                    title: 'Parts orders',
                    icon: mdiPackageVariantClosed,
                    link: '/your-account/parts-orders-history'
                },
                {
                    id: 'products',
                    title: 'Registered products',
                    icon: mdiLaptop,
                    link: '/your-account/product-registration'
                },
                {
                    id: 'quotes',
                    title: 'Saved quotes',
                    icon: mdiClipboardTextOutline,
                    link: '/your-account/saved-quotes'
                },
                { id: 'happyfox', title: 'Support tickets', icon: mdiLifebuoy, link: '/your-account/support-tickets' },
                { id: 'organization', title: 'Organization', icon: mdiAccountGroupOutline }
            ]
        }
    },
    computed: {
        firstName() {
            return this.$auth?.user?.firstName || this.$auth?.user?.given_name || ''
        },
        dashboardSections() {
            return this.sections.map((section) => ({
                ...section,
                metric: '…',
                metricLabel: '',
                ...this.summaries[section.id],
                link: section.id === 'organization' ? this.organizationLink : section.link
            }))
        }
    },
    methods: {
        updateSummary(id, summary) {
            this.$set(this.summaries, id, summary)
        }
    }
}
</script>

<template>
    <main class="account-dashboard">
        <v-container class="account-dashboard__container">
            <header class="dashboard-intro">
                <div>
                    <span class="dashboard-eyebrow">GETAC / YOUR SERVICE WORKSPACE</span>
                    <h1
                        >Welcome back<span v-if="firstName">, {{ firstName }}</span
                        >.</h1
                    >
                    <p>Your devices. Your team. <span>A clearer view of what’s next.</span></p>
                </div>
                <router-link to="/create-rma" class="dashboard-primary-action">
                    <v-icon aria-hidden="true">{{ mdiPlus }}</v-icon>
                    Create a service request
                    <v-icon aria-hidden="true">{{ mdiArrowRight }}</v-icon>
                </router-link>
            </header>

            <div class="dashboard-context" role="note">
                Details reflect your signed-in account and organization access.
            </div>

            <nav class="dashboard-overview" aria-label="Account sections">
                <router-link
                    v-for="section in dashboardSections"
                    :key="section.id"
                    :to="section.link"
                    class="dashboard-stat"
                >
                    <span class="dashboard-stat__top">
                        <v-icon aria-hidden="true">{{ section.icon }}</v-icon>
                        <span>{{ section.title }}</span>
                    </span>
                    <strong>{{ section.metric }}</strong>
                    <span class="dashboard-stat__label">{{ section.metricLabel }}</span>
                </router-link>
            </nav>

            <div class="dashboard-section-label">
                <h2>Account overview</h2>
                <span>ALL YOUR SERVICE DETAILS, CONNECTED</span>
            </div>

            <div class="dashboard-panels">
                <rmas-section @summary="updateSummary('rmas', $event)" />
                <parts-orders-section @summary="updateSummary('parts', $event)" />
                <registered-products-section @summary="updateSummary('products', $event)" />
                <saved-quotes-section @summary="updateSummary('quotes', $event)" />
                <happyfox-section @summary="updateSummary('happyfox', $event)" />
                <organization-section
                    @summary="updateSummary('organization', $event)"
                    @navigation="organizationLink = $event"
                />
            </div>

            <footer class="dashboard-support">
                <span>Need a hand with your devices or account?</span>
                <router-link to="/help-support"
                    >Getac Support <v-icon aria-hidden="true">{{ mdiArrowRight }}</v-icon></router-link
                >
            </footer>
        </v-container>
    </main>
</template>

<style scoped>
.account-dashboard {
    position: relative;
    isolation: isolate;
    min-height: 100vh;
    padding: 38px 0 54px;
    color: #f2f4fa;
    background: radial-gradient(ellipse at 90% 0, rgba(219, 81, 32, 0.15), transparent 40rem),
        linear-gradient(145deg, #292c35, #22252d 65%, #2a2d35);
    font-family: 'Work Sans', 'Roboto', sans-serif;
}
.account-dashboard::before {
    position: fixed;
    z-index: 0;
    inset: 0;
    background-image: url('https://storage.googleapis.com/gusa-assets/army-BG3.png');
    background-position: center;
    background-size: cover;
    content: '';
    filter: grayscale(1) brightness(0.8);
    mix-blend-mode: soft-light;
    opacity: 0.12;
    pointer-events: none;
}
.account-dashboard__container {
    position: relative;
    z-index: 1;
    max-width: 1260px;
}
.dashboard-intro {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 26px;
}
.dashboard-intro > div:first-child {
    position: relative;
    isolation: isolate;
}
.dashboard-intro > div:first-child::before {
    position: absolute;
    z-index: -1;
    top: -42px;
    left: 12%;
    width: 250px;
    height: 150px;
    background: radial-gradient(ellipse, rgba(219, 81, 32, 0.16), transparent 70%);
    content: '';
    pointer-events: none;
}
.dashboard-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #ff9a74;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1.7px;
}
.dashboard-eyebrow::before {
    width: 7px;
    height: 7px;
    background: #db5120;
    border-radius: 2px;
    box-shadow: 0 0 12px rgba(219, 81, 32, 0.55);
    content: '';
}
.dashboard-intro h1 {
    margin: 9px 0 10px;
    font-size: clamp(28px, 3.4vw, 42px);
    font-weight: 600;
    letter-spacing: -1.4px;
}
.dashboard-intro h1 span {
    color: #ff9a74;
}
.dashboard-intro p {
    margin: 0;
    color: #bbc0cc;
    font-size: 14px;
    line-height: 1.6;
}
.dashboard-intro p span {
    color: #f2a58b;
}
.dashboard-primary-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    flex-shrink: 0;
    padding: 13px 17px;
    color: #fff;
    background: linear-gradient(125deg, #e76638, #db5120);
    border: 1px solid rgba(255, 185, 154, 0.25);
    border-radius: 9px;
    box-shadow: 0 8px 24px rgba(219, 81, 32, 0.18);
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    transition: transform 160ms ease, box-shadow 160ms ease;
}
.dashboard-primary-action:hover {
    transform: translateY(-2px);
    box-shadow: 0 11px 28px rgba(219, 81, 32, 0.3);
}
.dashboard-primary-action .v-icon {
    color: inherit;
    font-size: 18px;
}
.dashboard-context {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 23px;
    color: #afb4c0;
    font-size: 12px;
    line-height: 1.5;
}
.dashboard-overview {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 13px;
    margin-bottom: 32px;
}
.dashboard-stat {
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 0;
    padding: 18px;
    color: #e8ebf2;
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.065), rgba(255, 255, 255, 0.025));
    border: 1px solid rgba(255, 255, 255, 0.11);
    border-radius: 12px;
    text-decoration: none;
    transition: border-color 160ms ease, background-color 160ms ease;
}
.dashboard-stat:hover {
    border-color: rgba(240, 120, 76, 0.5);
    background-color: rgba(219, 81, 32, 0.07);
}
.dashboard-stat__top {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 500;
}
.dashboard-stat__top .v-icon {
    color: #f0784c;
    font-size: 19px;
}
.dashboard-stat strong {
    font-size: 32px;
    font-weight: 500;
    letter-spacing: -1px;
    line-height: 1.2;
    font-variant-numeric: tabular-nums;
}
.dashboard-stat__label {
    color: #adb3c1;
    font-size: 11px;
    line-height: 1.5;
}
.dashboard-section-label {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 17px;
}
.dashboard-section-label h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 500;
}
.dashboard-section-label > span {
    color: #8e96a6;
    font-size: 9px;
    letter-spacing: 1.2px;
}
.dashboard-panels {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
}
.dashboard-support a {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    flex-shrink: 0;
    color: #f2a58b;
    font-size: 12px;
    font-weight: 500;
    text-decoration: none;
}
.dashboard-support .v-icon {
    color: inherit;
    font-size: 16px;
}
.dashboard-support a:hover {
    text-decoration: underline;
}
.dashboard-support {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 28px;
    color: #a8b0bf;
    font-size: 12px;
}
.account-dashboard a:focus-visible {
    outline: 2px solid #f0784c;
    outline-offset: 4px;
}
@media (max-width: 1000px) {
    .dashboard-overview {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
    .dashboard-intro {
        align-items: flex-start;
    }
}
@media (max-width: 700px) {
    .account-dashboard {
        padding-top: 22px;
    }
    .dashboard-intro {
        flex-direction: column;
        gap: 18px;
    }
    .dashboard-panels {
        grid-template-columns: minmax(0, 1fr);
    }
    .dashboard-overview {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 10px;
    }
    .dashboard-stat:last-child {
        grid-column: 1 / -1;
    }
    .dashboard-section-label > span {
        display: none;
    }
}
@media (max-width: 420px) {
    .dashboard-primary-action {
        width: 100%;
    }
    .dashboard-stat {
        padding: 14px;
    }
}
@media (prefers-reduced-motion: reduce) {
    .dashboard-stat {
        transition: none;
    }
}
</style>
